import { useRef, useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router'
import styled from 'styled-components'
import { useCookies } from 'react-cookie'

import { Title, ProgressBar, SampleProofImg, TodoItem, FirstCertificationModal } from './components'
import ImgCropperModal from 'components/containers/task/ImgCropperModal'
import CenterModal from 'components/presenters/modal/CenterModal'
import TaskResultFormModal from 'components/presenters/task/modal/TaskResultFormModal'
import waterSample from 'images/onboarding_sample_water.png'
import pillSample from 'images/onboarding_sample_pill.png'
import walkSample from 'images/onboarding_sample_walk.png'
import { convertHeicFile } from 'utils/utils'
import ImageMergeProcessor from 'components/containers/common/ImageMergeProcessor'
import TaskResultTimeStamp from 'components/containers/task/TaskResultTimeStamp'
import LoadingModal from 'components/presenters/modal/LoadingModal'
import useUploadOnboardingImageMutate from 'hooks/queries/file/useUploadOnboardingImageMutate'
import { sendWebViewMsg } from 'utils/bridge'

const OnboardingStep3 = () => {
  const fileInputRef = useRef(null)

  const [cookies, setCookie] = useCookies()

  const {
    state: { habit },
  } = useLocation()

  const history = useHistory()

  const [isCropModalOpened, setIsCropModalOpened] = useState(false)
  const [originalImg, setOriginalImg] = useState(null)
  const [croppedImg, setCroppedImg] = useState(null)
  const [croppedBase64Img, setCroppedBase64Img] = useState(null)
  const [isImgConverting, setIsImgConverting] = useState(false)
  const [isImgCropping, setIsImgCropping] = useState(false)
  const [isImgMerging, setIsImgMerging] = useState(false)
  const [imagesToBeMerged, setImagesToBeMerged] = useState([])
  const [mergedImg, setMergedImg] = useState(null)

  const [isTaskResultModalOpened, setIsTaskResultModalOpened] = useState(false)

  const [centerModalVisible, setCenterModalVisible] = useState(false)
  const [centerModalDataList, setCenterModalDataList] = useState({})
  const [onModalComplete, setOnModalComplete] = useState(null)
  const [onModalCancel, setOnModalCancel] = useState(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [description, setDescription] = useState('')

  const [isFirstCertificationModal, setIsFirstCertificationModal] = useState(false)

  const { mutateAsync: uploadOnboardingImage } = useUploadOnboardingImageMutate()

  const headerText =
    habit === 'water'
      ? '물 마시기'
      : habit === 'pill'
      ? '영양제 먹기'
      : habit === 'walk'
      ? '산책하기'
      : ''

  const taskText =
    habit === 'water'
      ? '물 마시기'
      : habit === 'pill'
      ? '영양제 먹기'
      : habit === 'walk'
      ? '산책 하기'
      : ''

  const sampleImage =
    habit === 'water'
      ? waterSample
      : habit === 'pill'
      ? pillSample
      : habit === 'walk'
      ? walkSample
      : undefined

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleFileInputChanged = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      let targetImg = e.target.files[0]
      const fileExt = targetImg?.name?.split('.')?.slice(-1)?.pop()
      if (fileExt) {
        if (fileExt.toLowerCase() === 'heic') {
          setIsImgConverting(true)
          targetImg = await convertHeicFile(targetImg)
          setIsImgConverting(false)
        }
      }
      setOriginalImg(targetImg)
      if (isTaskResultModalOpened) {
        setIsTaskResultModalOpened(false)
      }
      setIsCropModalOpened(true)
    }
  }

  const isReadyToMerge = croppedBase64Img && imagesToBeMerged.length > 0

  const initImages = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null
    }

    setOriginalImg(null)
    setCroppedImg(null)
    setCroppedBase64Img(null)
    setImagesToBeMerged([])
    setMergedImg(null)
  }

  const initCenterModal = () => {
    setCenterModalVisible(false)
    setCenterModalDataList({})
    setOnModalCancel(null)
    setOnModalComplete(null)
  }

  const handleSubmitClick = async () => {
    setIsSubmitting(true)

    try {
      const [
        {
          data: {
            upload_results: [{ file_name: originalFileName, path: originalFilePath }],
          },
        },
        {
          data: {
            upload_results: [{ file_name: mergedFileName, path: mergedFilePath }],
          },
        },
      ] = await Promise.all([
        uploadOnboardingImage({ path: 'task_original_img/', files: croppedImg }),
        uploadOnboardingImage({ path: 'task_img/', files: mergedImg }),
      ])

      setCookie('onboarding_habit', habit, { path: '/', maxAge: 60 * 60 })
      setCookie('onboarding_description', description ?? '', { path: '/', maxAge: 60 * 60 })
      setCookie('onboarding_original_image_name', originalFileName, { path: '/', maxAge: 60 * 60 })
      setCookie('onboarding_original_image_path', originalFilePath, { path: '/', maxAge: 60 * 60 })
      setCookie('onboarding_merged_image_name', mergedFileName, { path: '/', maxAge: 60 * 60 })
      setCookie('onboarding_merged_image_path', mergedFilePath, { path: '/', maxAge: 60 * 60 })

      setIsSubmitting(false)
      setIsTaskResultModalOpened(false)
      setIsFirstCertificationModal(true)
    } catch (e) {
      setIsSubmitting(false)

      setCenterModalVisible(true)
      setCenterModalDataList({
        description: '이미지 등록을 실패했어요.\n 다시 시도해주세요.',
      })
      setOnModalComplete(() => () => {
        initCenterModal()
        initImages()
      })
    }
  }

  return (
    <>
      <Wrapper>
        <ProgressBarBlock>
          <ProgressBar currentStep={3} />
        </ProgressBarBlock>
        <Title>{`${headerText},\n간단히 인증하면 끝!`}</Title>
        <ImageBlock>
          <SampleProofImg src={sampleImage} />
        </ImageBlock>
        <TodoItemBlock>
          <TodoItem
            taskName={taskText}
            onCameraClick={async () => {
              // fileInputRef.current.click()
              let response
              try {
                response = await sendWebViewMsg({
                  action: 'REQ_OPEN_CERTIFICATION',
                  data: {},
                })
              } catch (e) {
                console.log(e)
                if (
                  e.detail_error === 'APP_NOT_DETECTED' ||
                  e.error.res_data.err_code === 'ACTION_NOT_EXISTS'
                ) {
                  fileInputRef.current.click()
                }
                return
              }

              if (response.success !== 1) {
                setCenterModalVisible(true)
                setCenterModalDataList({
                  description: '이미지 등록을 실패했어요.\n 다시 시도해주세요.',
                })
                setOnModalComplete(() => () => {
                  initCenterModal()
                  initImages()
                })
                return
              }

              setIsSubmitting(true)

              const { proof, original_proof: originalProof, description } = response.data

              try {
                const [
                  {
                    data: {
                      upload_results: [{ file_name: originalFileName, path: originalFilePath }],
                    },
                  },
                  {
                    data: {
                      upload_results: [{ file_name: mergedFileName, path: mergedFilePath }],
                    },
                  },
                ] = await Promise.all([
                  uploadOnboardingImage({ path: 'task_original_img/', files: originalProof }),
                  uploadOnboardingImage({ path: 'task_img/', files: proof }),
                ])

                setCookie('onboarding_habit', habit, { path: '/', maxAge: 60 * 60 })
                setCookie('onboarding_description', description ?? '', {
                  path: '/',
                  maxAge: 60 * 60,
                })
                setCookie('onboarding_original_image_name', originalFileName, {
                  path: '/',
                  maxAge: 60 * 60,
                })
                setCookie('onboarding_original_image_path', originalFilePath, {
                  path: '/',
                  maxAge: 60 * 60,
                })
                setCookie('onboarding_merged_image_name', mergedFileName, {
                  path: '/',
                  maxAge: 60 * 60,
                })
                setCookie('onboarding_merged_image_path', mergedFilePath, {
                  path: '/',
                  maxAge: 60 * 60,
                })

                setIsSubmitting(false)
                setIsTaskResultModalOpened(false)
                setIsFirstCertificationModal(true)
              } catch (e) {
                setIsSubmitting(false)

                setCenterModalVisible(true)
                setCenterModalDataList({
                  description: '이미지 등록을 실패했어요.\n 다시 시도해주세요.',
                })
                setOnModalComplete(() => () => {
                  initCenterModal()
                  initImages()
                })
              }
            }}
          />
        </TodoItemBlock>
      </Wrapper>

      <ImgFileInput
        id={'onboardingTask'}
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileInputChanged}
      />

      <ImgCropperModal
        visible={isCropModalOpened}
        srcImg={originalImg}
        croppedImg={croppedImg}
        setCroppedImg={setCroppedImg}
        onCancel={(e) => {
          initImages()
          setIsCropModalOpened(false)
        }}
        onComplete={(e) => {
          setIsImgCropping(true)

          const reader = new FileReader()

          reader.onloadend = (event) => {
            event.preventDefault()
            setCroppedBase64Img(reader.result)
            setImagesToBeMerged([reader.result])

            setIsImgCropping(false)
          }

          reader.readAsDataURL(croppedImg)

          setIsCropModalOpened(false)
          setIsTaskResultModalOpened(true)
        }}
        onError={(error) => {
          if (error['detail_code'] === 'IMG_NOT_LOADED') {
            setCenterModalVisible(true)
            setCenterModalDataList({
              description: '이미지 로드를 실패했어요.\n 다시 시도해주세요.',
            })
            setOnModalComplete(() => () => {
              initCenterModal()
              initImages()
            })
          } else {
            setCenterModalVisible(true)
            setCenterModalDataList({
              description: '이미지 등록을 실패했어요.\n 다시 시도해주세요.',
            })
            setOnModalComplete(() => () => {
              initCenterModal()
              initImages()
            })
          }
        }}
      />

      {isReadyToMerge && (
        <ImageMergeProcessor
          base64Images={imagesToBeMerged}
          domTemplate={[<TaskResultTimeStamp key={'onboarding-task-timestamp'} />]}
          onComplete={(mergedImg) => {
            setMergedImg(mergedImg)
            setIsImgMerging(false)
          }}
          onProcessing={() => {
            //onMerge
            setIsImgMerging(true)
          }}
          onError={() => {
            setCenterModalVisible(true)
            setCenterModalDataList({
              description: '이미지 로드를 실패했어요.\n 다시 시도해주세요.',
            })
            setOnModalComplete(() => () => {
              initCenterModal()
              initImages()
            })
          }}
        />
      )}

      {isTaskResultModalOpened && (
        <TaskResultFormModal
          description={description}
          onChangeDescription={(value) => {
            setDescription(value)
          }}
          isValid={!!mergedImg}
          isImgLoading={isImgConverting || isImgCropping || isImgMerging}
          displayImg={mergedImg}
          onSubmit={() => {
            handleSubmitClick()
          }}
          onClose={() => {
            setCenterModalVisible(true)
            setCenterModalDataList({
              title: '게시물 작성 취소',
              description: '게시물 작성을 취소하시겠어요?',
            })
            setOnModalComplete(() => () => {
              initCenterModal()
              initImages()
              setIsTaskResultModalOpened(false)
            })
            setOnModalCancel(() => () => {
              initCenterModal()
            })
          }}
          onSelectImg={() => {
            fileInputRef.current.click()
          }}
          onCancelImg={() => {
            setCenterModalVisible(true)
            setCenterModalDataList({
              title: '사진 삭제',
              description: '사진을 삭제하시겠어요?',
            })
            setOnModalComplete(() => () => {
              initCenterModal()
              initImages()
            })
            setOnModalCancel(() => () => {
              initCenterModal()
            })
          }}
        />
      )}

      {isSubmitting && <LoadingModal zIndex={500} loadingTxt={'업로드중...'} />}

      <CenterModal
        modalVisible={centerModalVisible}
        setModalVisible={setCenterModalVisible}
        dataList={centerModalDataList}
        onCancel={onModalCancel}
        onComplete={onModalComplete}
      />

      {isFirstCertificationModal && (
        <FirstCertificationModal
          onStartClick={() => {
            setCookie('display_challenge_recommend_tooltip', true, { path: '/' })
            setCookie('display_motibell_tooltip', true, { path: '/' })
            history.push('/social?redirect_path=task/list', { isOnboarding: true })
          }}
        />
      )}
    </>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  padding-top: calc(100vw * (24 / 375));
  padding-right: calc(100vw * (20 / 375));
  padding-left: calc(100vw * (20 / 375));
`

const ProgressBarBlock = styled.div`
  margin-bottom: calc(100vw * (36 / 375));
`

const ImageBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: calc(100vw * (9 / 375));
  margin-top: calc(100vw * (55 / 375));
`

const TodoItemBlock = styled.div`
  margin-top: calc(100vw * (40 / 375));
  margin-bottom: 0;
  padding-right: calc(100vw * (10 / 375));
  padding-left: calc(100vw * (10 / 375));
  width: 100%;
  height: 100%;
`
const ImgFileInput = styled.input`
  display: none;
`

export default OnboardingStep3
